import React, { useEffect, useState } from 'react';
import { Input, MessageBarBody, MessageBarTitle, Textarea } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { ErrorType } from '../../../models/HttpError';

// guía
import { GuideDrawer, GuideDrawerForm, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// lógica
import { PRICE_LIST_NAME_MAX, validatePriceListName } from '../PriceList.Logic';

const COMMENTS_MAX = 500;

export interface ProviderPriceListFormProps {
  open: boolean;
  providerName: string;
  saving: boolean;
  error?: ErrorType;
  onDismiss: () => void;
  /** Alta: nombre y comentario ya recortados. */
  onCreate: (listName: string, comments: string) => void;
}

/**
 * Alta de una lista de precios del proveedor (guía, drawer small): antes se creaba con un
 * nombre fijo y había que renombrarla después; ahora se pide el nombre (obligatorio) y un
 * comentario opcional, con error debajo del campo y banner si el servidor falla.
 */
export const ProviderPriceListFormComponent = ({ open, providerName, saving, error, onDismiss, onCreate }: ProviderPriceListFormProps) => {
  const [t] = useTranslation();
  const [name, setName] = useState('');
  const [comments, setComments] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) { setName(''); setComments(''); setSubmitted(false); }
  }, [open]);

  const nameError = submitted ? validatePriceListName(name) : undefined;
  const dirty = name.trim().length > 0 || comments.trim().length > 0;

  const submit = () => {
    setSubmitted(true);
    if (validatePriceListName(name)) return;
    onCreate(name.trim(), comments.trim());
  };

  return (
    <GuideDrawer
      open={open}
      size="small"
      eyebrow={providerName}
      title={t('providerPriceListForm.title')}
      subtitle={t('providerPriceListForm.subtitle')}
      onDismiss={onDismiss}
      preventDismiss={saving}
      confirmDiscard={dirty && !saving}
      testId="provider-price-list-drawer"
      secondaryAction={{ label: t('providerPriceListForm.cancel'), onClick: onDismiss }}
      primaryAction={{ label: t('providerPriceListForm.create'), onClick: submit, loading: saving }}
    >
      {submitted && error && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="provider-price-list-error">
          <MessageBarBody>
            <MessageBarTitle>{t('providerPriceListForm.saveFailed')}</MessageBarTitle>
            {error.ErrorMessage}
          </MessageBarBody>
        </GuideMessageBar>
      )}
      <GuideDrawerForm onSubmit={submit} testId="provider-price-list-form">
        <GuideDrawerSection title={t('providerPriceListForm.section')}>
          <GuideField
            label={t('providerPriceListForm.name')}
            required
            hint={t('providerPriceListForm.nameHint')}
            counter={{ value: name.length, max: PRICE_LIST_NAME_MAX }}
            error={nameError ? t(nameError === 'required' ? 'providerPriceListForm.nameRequired' : 'providerPriceListForm.nameTooLong', { max: PRICE_LIST_NAME_MAX }) : undefined}
          >
            <Input
              autoFocus
              value={name}
              maxLength={PRICE_LIST_NAME_MAX}
              placeholder={t('providerPriceListForm.namePlaceholder')}
              disabled={saving}
              onChange={(_event, data) => setName(data.value)}
              data-testid="provider-price-list-name"
            />
          </GuideField>
          <GuideField label={t('providerPriceListForm.comments')} hint={t('providerPriceListForm.commentsHint')} counter={{ value: comments.length, max: COMMENTS_MAX }}>
            <Textarea
              value={comments}
              maxLength={COMMENTS_MAX}
              resize="vertical"
              disabled={saving}
              onChange={(_event, data) => setComments(data.value)}
            />
          </GuideField>
        </GuideDrawerSection>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default ProviderPriceListFormComponent;
